import React from 'react';
import { ScheduledEventCard } from 'shared/components/overview/ScheduledEvent';
import { Box, Flex, Text, Button } from '@opendoor/bricks/core';
import { getHubSSVAUploadRoute } from 'shared/helpers/routes';
import { ZonedDateTime } from '@js-joda/core';
import { InternalLink } from 'platform/react-router';
import { SSVAData } from 'sellDirect/OfferContext';

interface ORVARescheduleProps {
  offerReviewAt: ZonedDateTime | null;
  onReschedule?: () => void;
  ssvaState: SSVAData['ssvaState'];
  shouldSeeSsvaNooExperience?: boolean;
  /** Optional title. Defaults to Video Walkthrough */
  title?: string;
  /** Optional description. */
  description?: string;
  /** Optional CTA text. Defaults to 'Reschedule' */
  ctaText?: string;
  /** Optional event duration. Defaults to 30*/
  eventDuration?: number;
}

/**
 * Shows the ORVA reschedule modal
 * renders a link to alternatively upload SSVA videos if they have not already started that process
 */
export const ORVAReschedule: React.FC<ORVARescheduleProps> = ({
  offerReviewAt,
  onReschedule,
  shouldSeeSsvaNooExperience,
  ssvaState,
  title,
  description,
  ctaText,
  eventDuration,
}) => {
  if (!offerReviewAt) {
    return null;
  }

  return (
    <Flex
      data-testid="orva-reschedule-content"
      height="100%"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box>
        <ScheduledEventCard
          title={title || 'Video walkthrough'}
          scheduleTime={offerReviewAt}
          eventDuration={eventDuration || 30}
        />
        <Text color="neutrals100" fontSize="s1" pt={6} mb={10}>
          {description ||
            `We’ll walk through your home together over a 30-minute video call. Afterwards, an expert\
          from our team will review your info, so we can make our best offer.`}
        </Text>
      </Box>
      <Box>
        {/* Reschedule button */}
        <Button
          aria-label="Reschedule video walkthrough"
          analyticsName="cake-reschedule-orva-modal"
          onClick={() => {
            onReschedule && onReschedule();
          }}
        >
          {ctaText || 'Reschedule'}
        </Button>
        {shouldSeeSsvaNooExperience && ssvaState == 'not_started' && (
          <Box mt="5">
            <Flex flexDirection="column" alignItems="center">
              <Text fontWeight="semibold">Prefer to do it on your free time?</Text>
              <InternalLink
                aria-label="Try a self-guided walkthrough"
                analyticsName="switch-to-ssva-from-orva-reschedule"
                to={getHubSSVAUploadRoute()}
                fontWeight="semibold"
              >
                Try a self-guided walkthrough
              </InternalLink>
            </Flex>
          </Box>
        )}
      </Box>
    </Flex>
  );
};
